import React, { forwardRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import type { CommentListProps, CommentListRef } from './CommentList.types';
import { LoadingState, EmptyState } from '../../components/CommentLoader';
import './CommentList.css';
import { useCommentList } from './useCommentList';
import { CommentSection } from './CommentSection';
import { CommentListLoadMore } from './CommentListLoadMore';

// Internal component without error boundary
const CommentListInternal = forwardRef<
  CommentListRef,
  Omit<CommentListProps, 'errorBoundaryProps'>
>(({ ...props }, ref) => {
  const {
    isLoading,
    containerProps,
    unresolvedComments,
    resolvedComments,
    getCardPropsFor
  } = useCommentList(props, ref);

  if (isLoading) return <LoadingState {...containerProps} />;
  if (props.comments.length === 0) return <EmptyState {...containerProps} />;

  const useVirtualization = props.useVirtualization;
  const isLoadMore =
    useVirtualization === undefined || useVirtualization?.type === 'LOAD_MORE';

  if (isLoadMore) {
    return (
      <CommentListLoadMore
        unresolvedComments={unresolvedComments}
        resolvedComments={resolvedComments}
        getCardPropsFor={getCardPropsFor}
        useVirtualization={useVirtualization}
        containerProps={containerProps}
      />
    );
  }

  return (
    <div {...containerProps}>
      <CommentSection
        comments={unresolvedComments}
        getCardPropsFor={getCardPropsFor}
      />

      {unresolvedComments.length > 0 && resolvedComments.length > 0 && (
        <div className="comment-list__divider" />
      )}

      <div className="comment-list__resolved-section">
        <CommentSection
          comments={resolvedComments}
          getCardPropsFor={getCardPropsFor}
        />
      </div>
    </div>
  );
});

CommentListInternal.displayName = 'CommentListInternal';

/**
 * CommentList - A container component with built-in error boundary protection
 * Automatically wraps the component with ErrorBoundary for crash protection
 */
export const CommentList = forwardRef<CommentListRef, CommentListProps>(
  ({ errorBoundaryProps, ...props }, ref) => {
    // If error boundary is disabled, return the internal component directly
    if (errorBoundaryProps?.disabled) {
      return <CommentListInternal ref={ref} {...props} />;
    }

    // Wrap with internal error boundary for automatic protection
    return (
      <ErrorBoundary
        componentName="CommentList"
        fallback={errorBoundaryProps?.fallback}
        onError={errorBoundaryProps?.onError}
        showErrorDetails={errorBoundaryProps?.showErrorDetails}
      >
        <CommentListInternal ref={ref} {...props} />
      </ErrorBoundary>
    );
  }
);

CommentList.displayName = 'CommentList';
